import {
  Box,
  Card,
  CardActionArea,
  CardActionAreaProps,
  CardHeader,
  CardHeaderProps,
  CardProps,
  Stack,
  StackProps,
} from '@mui/material';
import { FC, ReactElement } from 'react';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Heap } from 'clientServerShared/types/eventTracking';
import { RenderIf } from 'projects/sharedComponents/src/renderStates/renderIf';
import { getHeapDataAttributes } from 'projects/sharedComponents/src/services/eventTracking/eventTracking';
import { styled } from '@mui/material/styles';

type ClickableCardProps = {
  icon?: ReactElement;
  title?: CardHeaderProps['title'];
  subheader?: CardHeaderProps['subheader'];
  slotProps?: {
    card?: CardProps;
    cardHeader?: Omit<CardHeaderProps, 'title' | 'subheader'>;
    cardActionArea?: CardActionAreaProps & { href?: string };
    innerStack?: StackProps;
  };
} & Heap;

export const ClickableCard: FC<ClickableCardProps> = ({
  title,
  subheader,
  children,
  icon,
  heapMetadata,
  slotProps = {},
}) => {
  const { card = {}, cardHeader = {}, cardActionArea = {}, innerStack = {} } = slotProps;
  return (
    <StyledCard variant="outlined" {...card}>
      <StyledCardActionArea disableTouchRipple {...cardActionArea} {...getHeapDataAttributes(heapMetadata)}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={{ xs: 2, sm: 3 }}
          {...innerStack}
        >
          <RenderIf condition={!!icon}>{icon}</RenderIf>
          <Box width="100%">
            <StyledCardHeader
              title={title}
              subheader={subheader}
              titleTypographyProps={{
                variant: 'body1',
                fontWeight: 700,
              }}
              subheaderTypographyProps={{
                variant: 'body2',
                color: (theme) => theme.palette.helix.greyDark,
              }}
              {...cardHeader}
            />
            {children}
          </Box>
          <ChevronRightIcon aria-hidden="true" sx={{ fontSize: 32 }} />
        </Stack>
      </StyledCardActionArea>
    </StyledCard>
  );
};

const StyledCard = styled(Card)<CardProps>(({ theme }) => ({
  borderRadius: theme.spacing(1),
  border: '2px solid',
  borderColor: theme.palette.helix.greyLightest,
}));

const StyledCardHeader = styled(CardHeader)<CardHeaderProps>({
  padding: 0,
});

const StyledCardActionArea = styled(CardActionArea)(({ theme }) => ({
  'paddingTop': theme.spacing(2),
  'paddingBottom': theme.spacing(2),
  'paddingLeft': theme.spacing(2),
  'paddingRight': theme.spacing(1),
  '&:hover, &:focus': {
    color: 'inherit',
  },
  [theme.breakpoints.up('sm')]: {
    paddingLeft: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3),
    paddingRight: theme.spacing(2),
  },
}));
